import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Select, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/v2/spinner";
import { useGetAllEmployeesInfiniteQuery } from "@/services/(company)/userApi";
import type { FormikProps } from "formik";
import { startCase } from "lodash";
import { SearchIcon, User, XIcon } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useMemo, useState } from "react";
import { useOnInView } from "react-intersection-observer";
import { useDebounceValue } from "usehooks-ts";
import { Button } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import type { Fields } from ".";
import { cn } from "@/utils/cn";

const fieldName = "projectManagerIds" satisfies keyof Fields;
interface Props {
  formik: FormikProps<Fields>;
}

export const ProjectManagersField = ({ formik }: Props) => {
  const { t } = useTranslation(["projects", "common"]);
  const [isOpen, setIsOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounceValue(searchQuery, 500);
  const {
    fetchNextPage,
    data: apiResponse,
    isFetchingNextPage,
    isFetching: isFetchingEmployees,
    isLoading: isFirstLoadingEmployees,
  } = useGetAllEmployeesInfiniteQuery({
    limit: 500,
    search: debouncedSearch,
  });
  const employees = apiResponse?.pages.flatMap((page) => page.data) ?? [];
  const selectedEmpIds = formik.values.projectManagerIds || [];

  const employeesSortedBySelectedFirst = useMemo(() => {
    const initialSelectedIds = formik.values.projectManagerIds || [];
    return [...employees].sort((a, b) => {
      const isASelected = initialSelectedIds.includes(a.id);
      const isBSelected = initialSelectedIds.includes(b.id);
      if (isASelected && !isBSelected) return -1;
      if (!isASelected && isBSelected) return 1;
      return 0;
    });
    // Only re-calculate if the raw API data changes (e.g., pagination)
    // Using NOT to re render when slecting employees, to prevent user going to the top of the list on every selection
  }, [isOpen, apiResponse]);

  const toggleSelection = (id: number) => {
    const currentIds: (number | undefined)[] =
      formik.values.projectManagerIds || [];
    const nextValue = currentIds.includes(id)
      ? currentIds.filter(
          (item): item is number => item !== id && item !== undefined,
        )
      : [...currentIds, id];
    formik.setFieldValue(fieldName, nextValue);
  };

  const handleConfirm = () => {
    formik.setFieldValue(fieldName, selectedEmpIds);
    setIsOpen(false);
  };

  const clearSelection = () => formik.setFieldValue(fieldName, []);
  const clearSearchQuery = () => setSearchQuery("");
  const isSelectedEmployees = Boolean(selectedEmpIds.length);

  const infiniteScrollAnchor = useOnInView((inView) => {
    if (inView) fetchNextPage();
  });

  return (
    <>
      <Field className="col-span-1">
        <FieldLabel htmlFor={fieldName}>
          {t("projects:project_manager")}
          <span className="text-danger-500">*</span>
        </FieldLabel>
        <Select disabled={isFetchingEmployees || isFirstLoadingEmployees}>
          <SelectTrigger
            onClick={() => setIsOpen(true)}
            className={cn(
              formik?.values?.projectManagerIds?.length && "text-black!",
            )}
          >
            {formik.values.projectManagerIds &&
            formik.values.projectManagerIds?.length > 0 ? (
              <SelectValue
                placeholder={t("projects:managers_selected", {
                  count: formik.values.projectManagerIds.length,
                })}
              />
            ) : (
              <SelectValue
                placeholder={t("projects:select_project_managers")}
              />
            )}
          </SelectTrigger>
        </Select>
        <FieldError>
          {formik.touched.projectManagerIds &&
            (formik.errors.projectManagerIds as string)}
        </FieldError>
      </Field>
      <Dialog
        open={isOpen}
        onOpenChange={() => {
          setIsOpen((open) => !open);
          clearSearchQuery();
        }}
      >
        <DialogContent showCloseButton={false} aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t("projects:select_project_managers")}</DialogTitle>
          </DialogHeader>
          {/* Search */}
          <InputGroup>
            <InputGroupInput
              value={searchQuery}
              placeholder={t("projects:search_employees")}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroupAddon className="gap">
              <SearchIcon className="text-black/50" />
            </InputGroupAddon>
            <InputGroupAddon align="inline-end">
              {isFetchingEmployees && <Spinner size="sm" />}
              {!isFetchingEmployees && searchQuery.length > 0 && (
                <button onClick={clearSearchQuery} className="px-1">
                  <XIcon size={16} className="text-black/50" />
                </button>
              )}
            </InputGroupAddon>
          </InputGroup>

          {/* Select all & clear selection */}
          <div className="flex justify-between text-sm">
            {employeesSortedBySelectedFirst?.length > 0 && (
              <div className="flex gap-2">
                <p>
                  {t("projects:employees_count", {
                    count: apiResponse?.pages[0].meta.totalItems,
                  })}
                </p>
                {isSelectedEmployees && (
                  <p>
                    {t("projects:n_selected", { count: selectedEmpIds.length })}
                  </p>
                )}
                <button
                  type="button"
                  disabled={
                    formik.values.projectManagerIds?.length ===
                    employeesSortedBySelectedFirst.length
                  }
                  className="text-primary disabled:opacity-50 hover:underline disabled:pointer-events-none"
                  onClick={() => {
                    if (
                      employeesSortedBySelectedFirst &&
                      employeesSortedBySelectedFirst.length > 0
                    ) {
                      const allIds = employeesSortedBySelectedFirst
                        .map((emp) => emp.id)
                        .filter(
                          (id): id is number => id !== undefined && id !== null,
                        );
                      formik.setFieldValue(fieldName, allIds);
                    }
                  }}
                >
                  {t("common:buttons.select_all")}
                </button>
              </div>
            )}
            {isSelectedEmployees && (
              <button className="text-primary" onClick={clearSelection}>
                {t("projects:clear_selection")}
              </button>
            )}
          </div>

          <ul className="min-h-80 max-h-80 overflow-y-scroll space-y-2">
            {isFirstLoadingEmployees ? (
              <Spinner />
            ) : employeesSortedBySelectedFirst?.length > 0 ? (
              <>
                {employeesSortedBySelectedFirst.map((emp) => (
                  <li
                    key={emp.id}
                    role="button"
                    className="flex items-center space-x-4 hover:bg-primary/10 rounded-xxs p-1"
                  >
                    <Checkbox
                      checked={selectedEmpIds.includes(emp.id)}
                      onCheckedChange={() => toggleSelection(emp.id)}
                    />
                    <Avatar onClick={() => toggleSelection(emp.id)}>
                      <AvatarImage
                        src={emp?.profileImage ?? undefined}
                        alt={t("common:a11y.profile_picture_alt")}
                      />
                      <AvatarFallback>
                        <User />
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className="flex flex-col"
                      onClick={() => toggleSelection(emp.id)}
                    >
                      <p>{startCase(emp.employeeName.toLowerCase())}</p>
                      <small className="text-black/50">{emp.position}</small>
                    </div>
                  </li>
                ))}
                {/* Load more employees */}
                <div ref={infiniteScrollAnchor} />
                {isFetchingNextPage && <Spinner wrapperClassName="h-5" />}
              </>
            ) : (
              <small className="block">
                {t("projects:no_employees_found")}
              </small>
            )}
          </ul>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                {t("common:buttons.cancel")}
              </Button>
            </DialogClose>
            <Button type="button" onClick={handleConfirm}>
              {t("common:buttons.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
